import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import { EXTERNAL_INTEGRATION_DISPLAY_NAME } from 'dashboard/models/external-integration';
import { useListExternalIntegrationsQuery } from 'dashboard/redux/api/external-integration';
import { useListExternalIntegrationConfigsQuery } from 'dashboard/redux/api/external-integration-config';
import Card from 'design-system/components/Card/Card';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import { LEGACY_EXTERNAL_INTEGRATION_VENDORS } from 'lib/constants/externalIntegrationVendors';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

interface IntegrationDisplayData {
  id: string;
  type: string;
  status: string;
  name: string;
  vendorRoute: string;
  creatorName: string;
  createdAt: string;
}

const DashboardIntegrationsView = () => {
  const navigate = useNavigate();

  const { data: externalIntegrations, isLoading: isLoadingIntegration } =
    useListExternalIntegrationsQuery();
  const { data: externalIntegrationsConfigs, isLoading: isLoadingConfig } =
    useListExternalIntegrationConfigsQuery();

  const credentials = useMemo(() => {
    if (isLoadingIntegration || isLoadingConfig) {
      return [];
    }

    const mappedConfigs: IntegrationDisplayData[] = (externalIntegrationsConfigs ?? []).map(
      (config): IntegrationDisplayData => {
        return {
          id: config.id,
          type: config.attributes['vendor-title'],
          status: config.attributes['status'],
          name: config.attributes['label'],
          vendorRoute: `integrations/${config.attributes['vendor-name']}`,
          creatorName: config.attributes.creator,
          createdAt: config.attributes['created-at'],
        };
      },
    );

    const mappedIntegrations: IntegrationDisplayData[] =
      (externalIntegrations ?? [])
        .filter(({ type }) => !type.includes('data-source'))
        .map((integration): IntegrationDisplayData => {
          return {
            id: integration.id,
            type:
              integration.type in EXTERNAL_INTEGRATION_DISPLAY_NAME
                ? EXTERNAL_INTEGRATION_DISPLAY_NAME[
                    integration.type as keyof typeof EXTERNAL_INTEGRATION_DISPLAY_NAME
                  ]
                : integration.type,
            status: integration.attributes.status,
            name: integration.attributes.name,
            vendorRoute: `marketplace/${
              LEGACY_EXTERNAL_INTEGRATION_VENDORS.find(
                (vendor) => vendor.model === integration.type,
              )?.name || ''
            }`,
            creatorName: integration.attributes['creator-name'],
            createdAt: integration.attributes['created-at'],
          };
        }) || [];

    return mappedIntegrations.concat(mappedConfigs);
  }, [externalIntegrations, externalIntegrationsConfigs, isLoadingIntegration, isLoadingConfig]);

  return (
    <>
      <InactiveEnvironmentBanner />
      <Container>
        <Header title='Integrations' />
        <Section>
          <Card>
            <Card.Body>
              <Table isLoading={isLoadingIntegration || isLoadingConfig}>
                <TableHead sticky>
                  <TableRow>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Created by</TableHeaderCell>
                    <TableHeaderCell>Created at</TableHeaderCell>
                    <TableHeaderCell width='min' />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {credentials.map((credential) => (
                    <TableRow
                      id={`external-integration-${credential.id}`}
                      key={`integrations-show__config-row__${credential.id}`}
                      onClick={() => {
                        navigate(`/dashboard/${credential.vendorRoute}`);
                      }}
                    >
                      <TableCell>{credential.type}</TableCell>
                      <TableCell>
                        <Sticker variant={credential.status === 'active' ? 'green' : 'grey'}>
                          {titleCase(credential.status)}
                        </Sticker>
                      </TableCell>
                      <TableCell>{credential.name}</TableCell>
                      <TableCell>{credential.creatorName}</TableCell>
                      <TableCell>
                        {DateTime.fromISO(credential.createdAt).toFormat(DATE_AND_TIME_FORMAT)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {credentials.length === 0 && (
                <div
                  style={{
                    display: 'flex',
                    height: '176px',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'auto',
                  }}
                >
                  <Paragraph size='lg' weight='bold' style={{ color: 'black' }}>
                    Nothing to see here yet
                  </Paragraph>
                  <Paragraph size='md'>Add a credential to get started</Paragraph>
                </div>
              )}
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardIntegrationsView;
